import { DataCellState } from 'domain/models';
import { initialDataCellState } from 'hooks/useActiveReportStore';
import React, { createContext, useContext, useMemo, useState, useCallback } from 'react';
import { Capabilities } from 'types/report';
import { TableActions } from './types';
import { act } from 'react-dom/test-utils';
import { useFlags } from 'launchdarkly-react-client-sdk';

type tooltip = {
  updating: boolean;
  tooltip: string | null;
};

export type actions = {
  difference: boolean;
  total: boolean;
  totalRow: boolean;
  constrained: boolean;
  trxModal: boolean;
  audit: boolean;
  trxModalData: DataCellState;
  detailsPanel: boolean;
};

type vFilters = {
  money: boolean;
  percentage: boolean;
  transactions: boolean;
  actuals: boolean;
  budget: boolean;
  variance: boolean;
  oneUnit: boolean;
  bigHeader: boolean;
};

export type AccountVisualFiltersProps = {
  accounts: boolean;
  percentageLegend: boolean;
  sparklines: boolean;
};

type trxModalProps = {
  modalOpen: boolean;
  audit: null;
  dataRows: [];
  date: {
    start: string;
    end: string;
  };
  title: string;
  parent: string;
  total: number;
  loading: boolean;
  hasDataRowsOrAudit: boolean;
};

const NTUtilsContext = createContext<{
  tooltip: tooltip;
  setTooltip: (tooltip: tooltip) => Promise<void>;
  actions: actions;
  setActions: (actions: actions) => void;
  capabilities: Capabilities;
  setCapabilities: (capabilities: Capabilities) => void;
  vFilters: vFilters;
  setVFilters: (vFilters: vFilters) => Promise<void>;
  accountVisualFilters: AccountVisualFiltersProps;
  setAccountVisualFilters: (AccountVisualFilters: AccountVisualFiltersProps) => void;
  featureFlags: Record<string, any>;
}>({
  tooltip: {
    updating: false,
    tooltip: '',
  },
  setTooltip: async () => {},

  actions: {
    difference: false,
    total: false,
    totalRow: false,
    constrained: true,
    trxModal: false,
    trxModalData: initialDataCellState,
    audit: false,
    detailsPanel: false,
  },
  setActions: () => {},

  capabilities: {
    anomalies: false,
    budget: false,
    charts: false,
    money: true,
    percentage: false,
    smoothing: false,
    sparklines: 'none',
    transactions: false,
    totalRow: false,
    variance: false,
    synthetic: false,
    flaggedTransactions: false,
    liveReport: false,
  },
  setCapabilities: () => {},

  vFilters: {
    money: true,
    percentage: false,
    transactions: false,
    actuals: true,
    budget: false,
    variance: false,
    oneUnit: false,
    bigHeader: false,
  },
  setVFilters: async () => {},

  accountVisualFilters: {
    accounts: false,
    percentageLegend: true,
    sparklines: true,
  },
  setAccountVisualFilters: () => {},
  featureFlags: {},
});
export const useNTUtilsContext = () => useContext(NTUtilsContext);

export const NTUtilsProvider: React.FC<{
  children: React.ReactNode;
  initialActions?: TableActions;
  initialAccountVisualFilters?: AccountVisualFiltersProps;
}> = ({ children, initialActions, initialAccountVisualFilters }) => {
  const [tooltip, setTooltip] = useState<tooltip>({
    updating: false,
    tooltip: '',
  });

  const setTooltipAsync = async (newTooltip: tooltip) => {
    setTooltip(newTooltip);
  };

  const [actionsState, setActionsState] = useState<actions>({
    difference: initialActions?.difference ?? false,
    total: initialActions?.total ?? false,
    totalRow: false,
    constrained: true,
    trxModal: false,
    trxModalData: initialDataCellState,
    audit: false,
    detailsPanel: initialActions?.detailsPanel ?? false,
  });

  // Wrapper function to log actions updates with useCallback for stability
  // No dependencies on actionsState to keep function stable
  const setActions = useCallback((newActions: actions) => {
    setActionsState(prevActions => {
      // Only update if there are actual changes to prevent unnecessary re-renders
      if (JSON.stringify(prevActions) !== JSON.stringify(newActions)) {
        // console.log('Actions updated:', { previous: prevActions, new: newActions });
        return newActions;
      }
      return prevActions;
    });
  }, []);

  // Use the stabilized actions value
  const actions = actionsState;

  const [capabilities, setCapabilities] = useState<Capabilities>({
    anomalies: false,
    budget: false,
    charts: false,
    money: true,
    percentage: false,
    smoothing: false,
    sparklines: 'none',
    transactions: false,
    totalRow: false,
    variance: false,
    synthetic: false,
    flaggedTransactions: false,
    liveReport: false,
  });

  const [vFilters, setVFilters] = useState<vFilters>({
    money: true,
    percentage: false,
    transactions: false,
    actuals: true,
    budget: false,
    variance: false,
    oneUnit: false,
    bigHeader: false,
  });

  const setVFiltersAsync = async (newVFilters: vFilters) => {
    setVFilters(newVFilters);
  };

  const [accountVisualFilters, setAccountVisualFilters] = useState<AccountVisualFiltersProps>({
    accounts: initialAccountVisualFilters?.accounts ?? false,
    percentageLegend: initialAccountVisualFilters?.percentageLegend ?? true,
    sparklines: initialAccountVisualFilters?.sparklines ?? true,
  });

  // Get ALL feature flags once at the context level
  // This prevents multiple individual flag subscriptions throughout the component tree
  const allFlags = useFlags();

  const contextValue = useMemo(
    () => ({
      tooltip,
      setTooltip: setTooltipAsync,
      actions,
      setActions,
      capabilities,
      setCapabilities,
      vFilters,
      setVFilters: setVFiltersAsync,
      accountVisualFilters,
      setAccountVisualFilters,
      featureFlags: allFlags, // Pass all flags through context
    }),
    [
      tooltip,
      actions,
      setActions,
      capabilities,
      vFilters,
      accountVisualFilters,
      setTooltipAsync,
      setVFiltersAsync,
      allFlags,
    ]
  );

  return <NTUtilsContext.Provider value={contextValue}>{children}</NTUtilsContext.Provider>;
};
